import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Chart, ChartModule } from 'angular-highcharts';
import { ArrayUtils } from "../../../common/providers/array-utils";
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppDatePipe } from '../../../common/pipes/app-date';
import { map, pairwise, startWith, tap } from 'rxjs/operators';
import { Answer } from "../../../common/models/answer";
import { EvaluationAthlete } from "../../../common/models/evaluation-athlete";
import { NgForOf, NgIf, NgStyle } from "@angular/common";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";

@Component({
  selector: 'evaluation-athlete-spider-chart',
  providers: [],
  styleUrls: ['./evaluation-athlete-spider-chart.scss'],
  standalone: true,
  imports: [
    NgStyle,
    NgForOf,
    ChartModule,
    NgIf,
    CenterLoaderComponent
  ],
  templateUrl: './evaluation-athlete-spider-chart.html'
})
export class SpiderChartComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input()
  public evaluationAthlete: EvaluationAthlete = {} as EvaluationAthlete;

  @Input()
  public selectedEvaluations$ = new EventEmitter<Set<any>>();

  @Input()
  public isDesktop = true;

  public chart: Chart = new Chart();
  public legendCategories: any[] = [];
  private seriesMap = new Map<any, number>();
  private seriesLength = 1;
  private initialized = false;

  private athleteValues: any[] = [];

  private subscriptions: Subscription[] = [];


  constructor(
    private arrayUtils: ArrayUtils,
    private datePipe: AppDatePipe,
    private translator: TranslateService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;
    if (changes['evaluationAthlete']) {
      this.resetChart();
      this.init();
      this.chartInit();
    }
  }

  public ngAfterViewInit() {
    this.init();
    this.chartInit();
    this.initialized = true;
  }

  public ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  private unsubscribeAll(): void {
    for (const s of this.subscriptions) s.unsubscribe();
    this.subscriptions = [];
  }

  private resetChart() {
    this.unsubscribeAll();
    this.chart = new Chart();
    this.athleteValues = [];
    this.legendCategories = [];
    this.seriesMap.clear();
    this.seriesLength = 1;
  }

  private init() {
    this.athleteValues = this.buildChartPoints(this.evaluationAthlete.answers);
    this.legendCategories = this.getUnique(
      (this.evaluationAthlete.answers ?? []).map((a) => ({
        name: a.question?.category?.name,
        color: a.question?.category?.color,
      })),
      'name'
    );
  }

  private buildChartPoints(answers: Answer[] | undefined): any[] {
    const sorted = [...(answers ?? [])].sort((a, b) => {
      const catA = a.question?.category?.id ?? Number.MAX_SAFE_INTEGER;
      const catB = b.question?.category?.id ?? Number.MAX_SAFE_INTEGER;
      if (catA !== catB) return catA - catB;
      return (a.question?.id ?? 0) - (b.question?.id ?? 0);
    });

    const grouped = this.arrayUtils.groupByAttribute(sorted, 'question', 'category', 'name');

    const points: any[] = [];
    if (grouped) {
      for (const category of Object.keys(grouped)) {
        for (const answer of grouped[category]) {
          points.push({
            name: answer.question.title,
            y: answer.value,
            categoryColor: answer.question.category?.color,
          });
        }
      }
    }
    return points;
  }

  private chartInit() {
    // http://jsfiddle.net/NalinS/0zr797v4/
    this.chart = new Chart({
      chart: {
        margin: (this.isDesktop ? [0, 30, 15, 30] : [0, 0, 5, 0]),
        polar: true,
        type: 'xy',
        renderTo: 'chart',
        events: {
          load() {
            const chart: any = this;
            const seriesData = this.xAxis[0].series[0].data as any[];
            const parts = seriesData.length;

            let centerX: number = chart.plotLeft + chart.yAxis[0]?.center?.[0];
            let centerY: number = chart.plotTop + chart.yAxis[0]?.center?.[1];
            let outerRadius: number = chart.yAxis[0].height;

            if (isNaN(centerX)) centerX = chart.plotWidth / 2;
            if (isNaN(centerY)) centerY = chart.plotHeight / 2;
            if (isNaN(outerRadius)) outerRadius = Math.min(chart.plotWidth, chart.plotHeight) / 2;

            const arcWidth = (2 * Math.PI) / parts;
            const baseAngle = -Math.PI / 2 - Math.PI / parts;

            let i = 0;
            for (const data of seriesData) {
              const innerRadius = 0;
              const start = baseAngle + arcWidth * i;
              const end = baseAngle + arcWidth * (i + 1);

              chart.renderer.arc(centerX, centerY, outerRadius, innerRadius, start, end).attr({
                fill: data.categoryColor
              }).add();

              i++;
            }

            // add mean value
            chart.renderer.circle(centerX, centerY, outerRadius / 2).attr({
              fill: 'rgba(255, 0, 0, 0.2)',
              stroke: 'rgba(255, 0, 0, 1)',
              'stroke-width': 1,
              zIndex: 1
            }).add();
          }
        }
      },
      title: {
        text: ''
      },
      pane: {
        size: '75%'
      },
      xAxis: {
        type: 'category',
        labels: {
          distance: 20
        }
      },
      yAxis: {
        min: 0,
        max: 10,
        tickInterval: 1,
        showLastLabel: false,
        labels: {
          step: 2
        }
      },
      tooltip: {
        headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
        pointFormat: '<span style="color:{point.color}">{point.name}</span>: <b>{point.y:.0f}</b><br/>'
      },
      plotOptions: {
        line: {
          dataLabels: {
            allowOverlap: true,
            enabled: true
          }
        },
        series: {
          dataLabels: {
            enabled: true,
            format: '{point.y:.0f}'
          }
        }
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        floating: true
      },
      credits: {
        enabled: false
      },
      lang: {
        noData: this.translator.instant('NoData'),
      }
    });

    // Add athlete values
    this.chart.addSeries({
      name: this.datePipe.transform(this.evaluationAthlete.date, 'mediumDate') ?? '',
      type: 'line',
      color: '#c29d4d',
      showInLegend: true,
      zIndex: 100,
      data: this.athleteValues,
    }, true, true);

    const series$ = this.selectedEvaluations$.pipe(
      startWith(new Set()), // used so that pairwise emits the first value immediately
      pairwise(), // emit previous and current value
      map(([pv, cv]) => {
        let removed = new Set<any>([...pv].filter(x => !cv.has(x)));
        let added = new Set<any>([...cv].filter(x => !pv.has(x)));
        return [removed, added];
      }), // map to difference between pv and cv
      tap(([removed, added]) => {
        // check if exists before removing
        for (const evaluation of removed) {
          if (this.seriesMap.has(evaluation)) {
            const idx = this.seriesMap.get(evaluation);
            this.seriesMap.delete(evaluation);
            for (const [key, value] of this.seriesMap) {
              if (value > idx!) {
                this.seriesMap.set(key, value-1);
              }
            }
            this.chart.removeSeries(idx!);
            this.seriesLength--;
          }
        }
        for (const athleteEvaluation of added) {
          // check if already added or not
          if (!this.seriesMap.has(athleteEvaluation) && athleteEvaluation.evaluation.id == this.evaluationAthlete.evaluationId) {
            this.chart.addSeries({
              name: this.datePipe.transform(athleteEvaluation.date, 'mediumDate') ?? '',
              type: 'line',
              showInLegend: true,
              zIndex: 100,
              data: this.buildChartPoints(athleteEvaluation['answers']),
            }, true, true);
            this.seriesMap.set(athleteEvaluation, this.seriesLength++);
          }
        }
      })
    );

    this.subscriptions.push(series$.subscribe());
  }

  public getUnique(arr: any, comp: any) {
    const unique = arr
      .map((e: any) => e[comp])
      .map((e: any, i: any, final: any) => final.indexOf(e) === i && i)
      .filter((e: any) => arr[e]).map((e: any) => arr[e]);

    return unique;
  }
}
